import React, { useEffect, useState } from 'react';
import { IoImageOutline } from 'react-icons/io5';

const BASE = 'https://firebasestorage.googleapis.com/v0/b/trankllc-com.appspot.com/o';

type StoreImageProps = {
  // stores ドキュメントID（一覧の data.image / 編集の editInfo.id）
  image: string;
  // stores.last_time（ms）。画像更新のたびに変わるのでキャッシュバストに使う
  // （見学の担当者写真の photoUpdatedAt と同じ考え方、18.visitInfo/shared.ts参照）。
  cacheKey?: number;
  alt?: string;
  // <img> に付けるクラス
  className?: string;
  // 読み込み中プレースホルダーの div に付けるクラス
  placeholderClassName: string;
  // プレースホルダーに文言を出すか（大きい枠向け。一覧の小サイズでは false）
  loadingText?: boolean;
};

const RETRY_MS = 2000;
const MAX_RETRY = 20; // 約40秒

// 店舗画像は Cloud Functions（Resize Images）が生成する
// stores/resize_images/{id}_500x500 を表示する。アップロード直後はまだ生成が
// 終わっておらず 404 になるため、生成されるまで数秒おきに URL を貼り直して
// 再試行し、その間は「画像を読み込み中です」を表示する。
// （旧実装は 1 回失敗したら灰色のデフォルト画像に固定していた）
const StoreImage: React.FC<StoreImageProps> = ({ image, cacheKey, alt = '', className, placeholderClassName, loadingText }) => {
  const [ loaded, setLoaded ] = useState(false);
  const [ bust, setBust ] = useState(0);
  const [ givenUp, setGivenUp ] = useState(false);

  // image または cacheKey（画像更新時刻）が変わったらリセット。
  // image（storesドキュメントID）自体は編集しても変わらないため、cacheKey無しだと
  // 更新後も同じURLのままブラウザキャッシュが効いてしまう。
  useEffect(() => {
    setLoaded(false);
    setBust(0);
    setGivenUp(false);
  }, [image, cacheKey]);

  // 未ロードの間だけ、一定間隔で URL を貼り直して再読み込みさせる
  useEffect(() => {
    if (!image || loaded || givenUp) return;
    const timer = setInterval(() => {
      setBust((b) => {
        if (b >= MAX_RETRY) {
          setGivenUp(true);
          return b;
        }
        return b + 1;
      });
    }, RETRY_MS);
    return () => clearInterval(timer);
  }, [image, loaded, givenUp]);

  if (!image) {
    return (
      <div className={placeholderClassName}>
        <IoImageOutline />
        {loadingText && <span>画像がありません</span>}
      </div>
    );
  }

  const src = `${BASE}/stores%2Fresize_images%2F${image}_500x500?alt=media&token=${cacheKey ?? 0}_${bust}`;

  return (
    <>
      {!loaded && (
        <div className={placeholderClassName}>
          <IoImageOutline />
          {loadingText && <span>{givenUp ? '画像を表示できませんでした' : '画像を読み込み中です'}</span>}
        </div>
      )}
      <img
        className={className}
        src={src}
        alt={alt}
        onLoad={() => setLoaded(true)}
        style={loaded ? undefined : { display: 'none' }}
      />
    </>
  );
};

export default StoreImage;
